import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Trash2, Users, BellRing, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", address: "", doctor: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const [refills, setRefills] = useState([]);
  useEffect(() => {
    api.get("/customers/refill-reminders").then(({ data }) => setRefills(data)).catch(() => {});
  }, []);

  const refillWaLink = (r) => {
    const text = encodeURIComponent(`Namaste ${r.name} ji! Trustmeds Pharma (Kashipur) se — aapki medicines (${r.last_items.join(", ")}) khatam hone wali hongi. Dobara order karna ho to isi message ka reply kar dein ya call karein: 7428811670. Same-day home delivery!`);
    return `https://wa.me/91${String(r.phone).replace(/\D/g, "").slice(-10)}?text=${text}`;
  };

  const save = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false); setForm(empty); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/customers/${c.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      {refills.length > 0 && (
        <Card className="p-4 border-emerald-200 bg-emerald-50" data-testid="refill-reminders-card">
          <div className="flex items-center gap-2 mb-2">
            <BellRing className="w-4 h-4 text-emerald-700" />
            <span className="text-sm font-bold text-emerald-900">Refill Reminders — in customers ki dawa khatam hone wali hai ({refills.length})</span>
          </div>
          <div className="space-y-2">
            {refills.map((r) => (
              <div key={r.phone} className="bg-white rounded-xl border border-emerald-100 p-3 flex items-center gap-3 flex-wrap" data-testid={`refill-${r.phone}`}>
                <div className="flex-1 min-w-40">
                  <div className="text-sm font-bold text-slate-900">{r.name} <span className="font-mono-num font-normal text-slate-500">• {r.phone}</span></div>
                  <div className="text-[11px] text-slate-500">Last: {r.last_date} • {r.last_items.join(", ")}</div>
                </div>
                <a href={refillWaLink(r)} target="_blank" rel="noreferrer" data-testid={`refill-wa-${r.phone}`} className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1EBE5D] text-white text-xs font-bold px-3 py-2 rounded-full">
                  <MessageCircle className="w-3.5 h-3.5" /> Reminder Bhejo
                </a>
              </div>
            ))}
          </div>
        </Card>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500">{items.length} patients &amp; repeat customers on file.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn" className="bg-emerald-900 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-2" /> Add Customer</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Users className="w-4 h-4 text-emerald-700" /> New Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Name*" v={form.name} on={(v) => setForm({ ...form, name: v })} />
              <Field label="Phone*" v={form.phone} on={(v) => setForm({ ...form, phone: v })} />
              <Field label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} />
              <Field label="Address" v={form.address} on={(v) => setForm({ ...form, address: v })} />
              <Field label="Doctor" v={form.doctor} on={(v) => setForm({ ...form, doctor: v })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.name || !form.phone} className="bg-emerald-900 hover:bg-emerald-800">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone…" className="pl-9" />
        </div>
      </Card>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Customer</TableHead><TableHead>Phone</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead className="text-right">Visits</TableHead>
              <TableHead className="text-right">Spent</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell><div className="font-semibold">{c.name}</div><div className="text-xs text-slate-500">{c.email}</div></TableCell>
                  <TableCell className="font-mono-num">{c.phone}</TableCell>
                  <TableCell className="text-sm text-slate-500">{c.doctor || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num">{c.total_purchases}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-emerald-900">₹{Number(c.total_spent || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={() => remove(c)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No customers yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, v, on }) {
  return (<div><Label className="text-xs">{label}</Label><Input value={v} onChange={(e) => on(e.target.value)} className="mt-1 h-10" /></div>);
}
